import type { FileSkillOption } from '@/shell/api/queries'

/**
 * PRD-017 v0.19 §3.11 "Choosing the agent requires a skill" (Sean 2026-09-22; Seb UX review note 34, decision 6):
 * the Skill select's rows and the one validation the dialog runs on Create. Pure, so the rules are testable without
 * the dialog: a skill the agent may not run yet is listed disabled with its reason (never hidden, so the person sees
 * what exists and who unlocks it), and Create with an agent and no skill is refused with the PRD sentence.
 */

export const AGENT_REQUIRES_SKILL = 'An agent only works through a skill. Pick one, or assign a person.'

/** The short reason under a disabled row, in the person's words (the API sends `reasonText`; this is the fallback). */
export const SKILL_REASON_SHORT: Record<Exclude<FileSkillOption['reason'], null>, string> = {
  not_permitted: 'Needs permission from a CFO',
  needs_definitions: 'Needs Definitions for this file',
  no_permissions_version: 'Needs permissions from a CFO',
  unknown_skill: 'No longer in the catalog',
}

export interface SkillSelectItem { id: string, label: string, supportingText: string | undefined, isDisabled: boolean }

export function skillSelectItems(options: ReadonlyArray<FileSkillOption>): SkillSelectItem[] {
  return options.map(o => ({
    id: o.id,
    label: o.name,
    supportingText: o.runnable ? (o.summary ?? undefined) : (o.reason ? SKILL_REASON_SHORT[o.reason] : 'Cannot run here yet'),
    isDisabled: !o.runnable,
  }))
}

export function disabledSkillKeys(options: ReadonlyArray<FileSkillOption>): string[] {
  return options.filter(o => !o.runnable).map(o => o.id)
}

/** The reason Create is refused before anything is sent, or null when the agent assignment is complete (edge case 96). */
export function agentAssigneeProblem(assigneeIsAgent: boolean, skillId: string | null, options: ReadonlyArray<FileSkillOption> | null): string | null {
  if (!assigneeIsAgent)
    return null
  if (!skillId)
    return AGENT_REQUIRES_SKILL
  const chosen = options?.find(o => o.id === skillId)
  if (chosen && !chosen.runnable)
    return chosen.reasonText ?? (chosen.reason ? SKILL_REASON_SHORT[chosen.reason] : 'This skill cannot run on this file yet.')
  return null
}

/** The hint under the Skill select when nothing can run: the person is told the one thing that unlocks it. */
export function skillSelectHint(options: ReadonlyArray<FileSkillOption> | null, loading: boolean): string {
  if (loading || !options)
    return 'Loading the skills this agent may run…'
  if (options.length === 0)
    return 'No skills can run on this file yet.'
  if (options.every(o => !o.runnable)) {
    const reasons = new Set(options.map(o => o.reason))
    if (reasons.size === 1 && reasons.has('needs_definitions'))
      return 'Every skill needs Definitions for this file first.'
    return 'None of these can run here yet; a CFO sets the agent\'s permissions under the file\'s Agent section.'
  }
  return 'The agent runs this skill for the item; the run starts when you create it.'
}
